import { Alert, Box, Typography } from "@mui/material";
import type { FieldErrors } from "react-hook-form";

import type { InsuranceVerificationFormFields } from "../form/validation.zod";

interface ValidationDisplayProps {
  formErrors: FieldErrors<InsuranceVerificationFormFields>;
  validationWarnings: string[];
}

export default function ValidationDisplay({
  formErrors,
  validationWarnings,
}: ValidationDisplayProps) {
  const hasErrors = Object.keys(formErrors).length > 0;
  const hasWarnings = validationWarnings.length > 0;

  if (!hasErrors && !hasWarnings) {
    return null;
  }

  return (
    <>
      {/* Display validation errors */}
      {hasErrors && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              ❌ Please fix the following errors:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 3 }}>
              {Object.entries(formErrors).map(([field, error]) => (
                <Typography
                  key={field}
                  variant="body2"
                  component="li"
                  sx={{ mb: 0.5 }}
                >
                  {(error?.message as string) || `${field} has an error`}
                </Typography>
              ))}
            </Box>
          </Box>
        </Alert>
      )}

      {/* Display validation warnings */}
      {hasWarnings && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Please review the following warnings:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 3 }}>
              {validationWarnings.map((warning, _index) => (
                <Typography
                  key={warning}
                  variant="body2"
                  component="li"
                  sx={{ mb: 0.5 }}
                >
                  {warning}
                </Typography>
              ))}
            </Box>
          </Box>
        </Alert>
      )}
    </>
  );
}
